import { useState, useCallback, useEffect } from "react";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { LockScreen } from "./components/LockScreen";
import { HomeScreen } from "./components/HomeScreen";
import { ControlCenter } from "./components/ControlCenter";
import { NotificationCenter } from "./components/NotificationCenter";
import { PhotosApp } from "./components/ios-apps/PhotosApp";
import { MessagesApp } from "./components/ios-apps/MessagesApp";
import { VoiceMemosApp } from "./components/ios-apps/VoiceMemosApp";
import { NotesApp } from "./components/ios-apps/NotesApp";
import { TailscaleAuth, isTailscaleAuthenticated } from "./components/TailscaleAuth";
import { useIOSStore } from "./hooks/useIOSStore";
import type { IOSAppId } from "./types";

// Initialize Convex client with fallback
const CONVEX_URL = import.meta.env.VITE_CONVEX_URL || "https://helpful-ostrich-583.convex.cloud";
const convex = new ConvexReactClient(CONVEX_URL);

// App component mapping
const appComponents: Record<IOSAppId, React.FC<{ onClose: () => void }>> = {
  photos: PhotosApp,
  messages: MessagesApp,
  voicememos: VoiceMemosApp,
  notes: NotesApp,
  settings: () => null,
  calendar: () => null,
  contacts: () => null,
};

function IOSContainer() {
  const { isLocked, currentApp, unlock, lock, openApp, closeApp } = useIOSStore();
  const [showControlCenter, setShowControlCenter] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount
  // Allow bypass with URL param ?access=open for testing
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const bypassAuth = urlParams.get('access') === 'open';
    setIsAuthenticated(bypassAuth || isTailscaleAuthenticated());
  }, []);

  const handleAuthenticated = useCallback(() => {
    setIsAuthenticated(true);
  }, []);

  const handleUnlock = useCallback(() => {
    unlock();
  }, [unlock]);

  const handleAppOpen = useCallback(
    (appId: IOSAppId) => {
      openApp(appId);
    },
    [openApp]
  );

  const handleAppClose = useCallback(() => {
    closeApp();
  }, [closeApp]);

  // Show auth screen if not authenticated
  if (!isAuthenticated) {
    return <TailscaleAuth onAuthenticated={handleAuthenticated} />;
  }

  // Render current app if open
  if (currentApp) {
    const AppComponent = appComponents[currentApp];
    return <AppComponent onClose={handleAppClose} />;
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black font-sans">
      {/* Lock Screen */}
      {isLocked && <LockScreen onUnlock={handleUnlock} />}

      {/* Home Screen */}
      {!isLocked && (
        <HomeScreen
          onAppOpen={handleAppOpen}
          onShowControlCenter={() => setShowControlCenter(true)}
          onShowNotificationCenter={() => setShowNotificationCenter(true)}
        />
      )}

      {/* Control Center */}
      {showControlCenter && (
        <ControlCenter
          isOpen={showControlCenter}
          onClose={() => setShowControlCenter(false)}
        />
      )}

      {/* Notification Center */}
      {showNotificationCenter && (
        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={() => setShowNotificationCenter(false)}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <ConvexProvider client={convex}>
      <IOSContainer />
    </ConvexProvider>
  );
}

export default App;